'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

type Props = { enabled?: boolean; height?: number };

export default function HeaderLoadingBar({ enabled = true, height = 3 }: Props) {
  const pathname = usePathname() || '/';
  const search = useSearchParams()?.toString() || '';

  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);

  const growTimer = useRef<number | null>(null);
  const hideTimer = useRef<number | null>(null);
  const autoFinish = useRef<number | null>(null);
  const startedRef = useRef(false);

  const clearTimers = () => {
    if (growTimer.current) window.clearInterval(growTimer.current);
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
    if (autoFinish.current) window.clearTimeout(autoFinish.current);
    growTimer.current = null;
    hideTimer.current = null;
    autoFinish.current = null;
  };

  const start = () => {
    if (!enabled || startedRef.current) return;
    startedRef.current = true;
    clearTimers();
    setVisible(true);
    setProgress(8);

    growTimer.current = window.setInterval(() => {
      setProgress((p) => {
        const next = p < 60 ? p + 8 : p < 85 ? p + 2 : p;
        return next > 85 ? 85 : next;
      });
    }, 110);

    autoFinish.current = window.setTimeout(() => finish(), 2500);
  };

  const finish = () => {
    if (!startedRef.current) return;
    if (growTimer.current) window.clearInterval(growTimer.current);
    growTimer.current = null;
    if (autoFinish.current) window.clearTimeout(autoFinish.current);
    autoFinish.current = null;
    setProgress(100);
    hideTimer.current = window.setTimeout(() => {
      setVisible(false);
      setProgress(0);
      startedRef.current = false;
    }, 160);
  };

  useEffect(() => {
    if (!enabled) return;
    finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search, enabled]);

  useEffect(() => {
    const onStart = () => start();
    const onDone = () => finish();
    window.addEventListener('hc:route:start', onStart);
    window.addEventListener('hc:route:done', onDone);
    return () => {
      window.removeEventListener('hc:route:start', onStart);
      window.removeEventListener('hc:route:done', onDone);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onClickCapture = (ev: MouseEvent) => {
      if (!enabled) return;
      if (ev.defaultPrevented) return;
      if (ev.button !== 0) return;
      if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
      let el = ev.target as HTMLElement | null;
      while (el && el.tagName !== 'A') el = el.parentElement;
      if (!el) return;
      const a = el as HTMLAnchorElement;
      const href = a.getAttribute('href');
      if (!href || href.startsWith('#')) return;
      const url = new URL(href, location.href);
      if (url.origin !== location.origin) return;
      window.dispatchEvent(new CustomEvent('hc:route:start'));
    };
    document.addEventListener('click', onClickCapture, true);
    return () => document.removeEventListener('click', onClickCapture, true);
  }, [enabled]);

  useEffect(() => {
    const w = window as any;
    w.__app = w.__app || {};
    w.__app.loadingBarStart = () => window.dispatchEvent(new CustomEvent('hc:route:start'));
    w.__app.loadingBarDone = () => window.dispatchEvent(new CustomEvent('hc:route:done'));
  }, []);

  useEffect(() => {
    return () => clearTimers();
  }, []);

  return visible ? (
    <div
      aria-hidden
      className="pointer-events-none absolute left-0 right-0 -bottom-px z-[1100] overflow-hidden"
      style={{ height }}
    >
      <div
        style={{
          width: `${progress}%`,
          height: '100%',
          transition: 'width 110ms ease-out, opacity 150ms ease',
          transform: 'translateZ(0)',
          background: 'linear-gradient(90deg, #22d3ee 0%, #8b5cf6 35%, #f97316 65%, #22c55e 100%)',
          backgroundSize: '200% 100%',
        }}
      />
    </div>
  ) : null;
}
